import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertsNotifications = ({ vessel }) => {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  useEffect(() => {
    // Mock alerts data
    const mockAlerts = [
      {
        id: 1,
        type: 'critical',
        category: 'performance',
        title: 'Fuel Efficiency Degradation',
        message: 'Fuel consumption increased by 15% over the last 3 days. Immediate attention required.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        source: 'IoT Sensors',
        actionRequired: true,
        recommendations: [
          'Check engine performance parameters',
          'Inspect fuel injection system',
          'Review recent maintenance logs'
        ]
      },
      {
        id: 2,
        type: 'warning',
        category: 'compliance',
        title: 'CII Rating Risk',
        message: 'Current trajectory may result in D rating for 2024. Consider optimization measures.',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
        source: 'CII Calculator',
        actionRequired: true,
        recommendations: [
          'Implement speed optimization',
          'Review route planning',
          'Consider alternative fuels'
        ]
      },
      {
        id: 3,
        type: 'info',
        category: 'maintenance',
        title: 'Scheduled Maintenance Due',
        message: 'Engine maintenance scheduled for next port call in 5 days.',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
        source: 'Maintenance System',
        actionRequired: false,
        recommendations: []
      },
      {
        id: 4,
        type: 'warning',
        category: 'performance',
        title: 'Speed Optimization Opportunity',
        message: 'Current speed profile is 8% above optimal. Potential savings: $2,400/month.',
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
        source: 'AI Optimizer',
        actionRequired: false,
        recommendations: [
          'Reduce speed by 1.2 knots',
          'Adjust departure time',
          'Optimize weather routing'
        ]
      },
      {
        id: 5,
        type: 'success',
        category: 'performance',
        title: 'Efficiency Target Achieved',
        message: 'Monthly fuel efficiency target exceeded by 3.2%. Excellent performance!',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000), // 12 hours ago
        source: 'Performance Monitor',
        actionRequired: false,
        recommendations: []
      }
    ];

    setAlerts(mockAlerts);
  }, [vessel]);

  const getAlertStyles = (type) => {
    switch (type) {
      case 'critical':
        return {
          bg: 'bg-error/5 border-error/20',
          text: 'text-error',
          icon: 'AlertCircle'
        };
      case 'warning':
        return {
          bg: 'bg-warning/5 border-warning/20',
          text: 'text-warning',
          icon: 'AlertTriangle'
        };
      case 'info':
        return {
          bg: 'bg-accent/5 border-accent/20',
          text: 'text-accent',
          icon: 'Info'
        };
      case 'success':
        return {
          bg: 'bg-success/5 border-success/20',
          text: 'text-success',
          icon: 'CheckCircle'
        };
      default:
        return {
          bg: 'bg-muted border-border',
          text: 'text-muted-foreground',
          icon: 'Bell'
        };
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'performance':
        return 'Gauge';
      case 'compliance':
        return 'Shield';
      case 'maintenance':
        return 'Wrench';
      default:
        return 'Bell';
    }
  };

  const handleDismiss = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };

  const handleAction = (alert) => {
    console.log('Taking action for alert:', alert?.title);
    // Implement specific actions based on alert type
  };

  const filteredAlerts = alerts?.filter(alert => {
    if (dismissedAlerts?.has(alert?.id)) return false;
    if (filter === 'all') return true;
    return alert?.category === filter;
  });

  const alertCounts = {
    all: alerts?.filter(a => !dismissedAlerts?.has(a?.id))?.length,
    performance: alerts?.filter(a => !dismissedAlerts?.has(a?.id) && a?.category === 'performance')?.length,
    compliance: alerts?.filter(a => !dismissedAlerts?.has(a?.id) && a?.category === 'compliance')?.length,
    maintenance: alerts?.filter(a => !dismissedAlerts?.has(a?.id) && a?.category === 'maintenance')?.length
  };

  const filters = [
    { value: 'all', label: 'All Alerts', icon: 'Bell' },
    { value: 'performance', label: 'Performance', icon: 'Gauge' },
    { value: 'compliance', label: 'Compliance', icon: 'Shield' },
    { value: 'maintenance', label: 'Maintenance', icon: 'Wrench' }
  ];

  return (
    <div className="bg-card rounded-lg border p-6 shadow-maritime-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Alerts & Notifications</h3>
          <p className="text-sm text-muted-foreground">
            Real-time performance and compliance monitoring
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Active Alerts</div>
            <div className="text-lg font-bold text-foreground">
              {alertCounts?.all}
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="Settings" iconPosition="left">
            Configure
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {filters?.map((filterOption) => (
          <Button
            key={filterOption?.value}
            variant={filter === filterOption?.value ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilter(filterOption?.value)}
            iconName={filterOption?.icon}
            iconPosition="left"
          >
            {filterOption?.label}
            {alertCounts?.[filterOption?.value] > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-primary-foreground text-primary text-xs rounded-full">
                {alertCounts?.[filterOption?.value]}
              </span>
            )}
          </Button>
        ))}
      </div>
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {filteredAlerts?.map((alert) => {
          const alertStyles = getAlertStyles(alert?.type);
          return (
            <div 
              key={alert?.id}
              className={`p-4 rounded-lg border transition-maritime ${alertStyles?.bg}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <Icon 
                    name={alertStyles?.icon} 
                    size={20} 
                    strokeWidth={2}
                    className={alertStyles?.text}
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-foreground">
                        {alert?.title}
                      </h4>
                      <Icon 
                        name={getCategoryIcon(alert?.category)} 
                        size={14} 
                        strokeWidth={2}
                        className="text-muted-foreground"
                      />
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {alert?.message}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-3">
                      <span>Source: {alert?.source}</span>
                      <span>•</span>
                      <span>{alert?.timestamp?.toLocaleString()}</span>
                    </div>
                    
                    {alert?.recommendations?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm font-medium text-foreground mb-2">
                          Recommended Actions:
                        </div>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {alert?.recommendations?.map((rec, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <span className="text-accent font-medium">•</span>
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col space-y-2 ml-4">
                  {alert?.actionRequired && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAction(alert)}
                      iconName="Play"
                      iconPosition="left"
                    >
                      Take Action
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDismiss(alert?.id)}
                    iconName="X"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {filteredAlerts?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" strokeWidth={1} />
          <h4 className="text-lg font-medium text-foreground mb-2">All Clear!</h4>
          <p className="text-sm text-muted-foreground">
            {filter === 'all' ?'No active alerts for this vessel'
              : `No ${filter} alerts at this time`
            }
          </p>
        </div>
      )}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Smartphone" size={16} className="text-accent" strokeWidth={2} />
            <span className="text-sm text-foreground">
              Real-time notifications enabled for critical alerts
            </span>
          </div>
          <Button variant="ghost" size="sm" iconName="Bell" iconPosition="left">
            Notification Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertsNotifications;